import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  deleteUser,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import * as Sentry from "@sentry/react-native";
// A static import (not require) so the web app's bundler can alias it to
// web/src/firebase.web.js; config/firebase.js handles its own init failures.
import { auth, db, functions, COLLECTIONS } from "../config/firebase";

// Field-diagnostic logging for the post-verification session cascade
// (kicked-back-to-onboarding bug, July 2026). Deliberately shipped enabled —
// the evidence only shows up on real devices. Flip to false (or wire to
// remote config) once the fix is confirmed in production; __DEV__ builds
// keep their usual gated console.error logging regardless.
export const DIAG_LOGS = __DEV__;

// server-checkin-redesign.md §15 — deleteAccountWithoutPassword's entire
// former multi-step, client-orchestrated deletion cascade (event_attendees
// pre-check + rate-limited cleanup callable, Firestore profile delete, email
// reservation release, best-effort club_members/notifications/preferences/
// push_tokens cascade, then client-side deleteUser()) now runs server-side,
// atomically, inside this single callable — see
// firebase-functions/eventCheckIn.js's createDeleteMyAccountHandler doc
// comment for the full ordering and why no rate limit is needed.
const deleteMyAccountCallable = httpsCallable(functions, "deleteMyAccount");

let verifiedClaimSyncedForUid = null;

class AuthService {
  // Sign up a new user
  static async signUp(email, password, userData) {
    try {
      // Validate email domain - only miami.edu emails allowed (exact match, rejects subdomains)
      const emailLower = email?.toLowerCase();
      const domain = emailLower?.split("@")[1];
      if (!email || domain !== "miami.edu") {
        return {
          data: null,
          error: {
            code: "auth/invalid-email-domain",
            message:
              "Only University of Miami email addresses (@miami.edu) are allowed to sign up.",
          },
        };
      }

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const emailReservationRef = doc(db, COLLECTIONS.USER_EMAILS, emailLower);
      let reservationClaimed = false;

      // Any failure past this point leaves a signed-up-but-incomplete Auth
      // account (no display name, no email reservation, or no profile) that
      // would otherwise permanently squat the email with no way to retry —
      // roll everything created so far back so the caller can just try again.
      const rollbackNewAccount = async () => {
        if (reservationClaimed) {
          await deleteDoc(emailReservationRef).catch(() => {});
        }
        await deleteUser(user).catch(() => {});
      };

      // Update display name in Firebase Auth
      try {
        await updateProfile(user, {
          displayName: userData.full_name,
        });
      } catch (displayNameError) {
        await rollbackNewAccount();
        throw displayNameError;
      }

      // Claim the email-uniqueness reservation before creating the profile —
      // the users/{userId} create rule requires this doc to already exist,
      // so it must be committed first (Firestore rules can't see writes from
      // later in the same client call the way a single transaction could).
      try {
        await setDoc(emailReservationRef, { uid: user.uid, email: emailLower });
        reservationClaimed = true;
      } catch (reservationError) {
        // Firebase Auth already prevents duplicate accounts per email, so this is
        // mostly a defense-in-depth race — EXCEPT when a user_emails/{email} doc is
        // left dangling by an incomplete deletion (the account-orphaning bug: Auth
        // user + profile got deleted but the email reservation didn't). In that case
        // setDoc() hits an existing doc, Firestore treats it as an update, and
        // firestore.rules has no allow-update on user_emails, so this always fails
        // with permission-denied — the caller can't tell "email genuinely in use" from
        // "stale reservation from a botched deletion" apart from the error message.
        // We deliberately do NOT attempt a client-side reclaim here: rules forbid a
        // client updating another uid's user_emails doc, and a client-side reclaim
        // path would itself be an email-hijack risk. The real fix is Fix 2 (complete
        // cascades so this can't be left dangling) plus admin cleanup via
        // scripts/hard-delete-user.js if it ever recurs — this just gives a clearer,
        // more actionable message than a bare "already exists" while that happens.
        await rollbackNewAccount();
        const isPermissionDenied = reservationError?.code === "permission-denied";
        if (isPermissionDenied) {
          // permission-denied on this write is the genuine collision signature:
          // firestore.rules has no allow-update on user_emails, so it's what a
          // dangling/existing reservation (real collision or the orphaning bug
          // above) always produces for this exact write.
          return {
            data: null,
            error: {
              code: "auth/email-already-in-use",
              message:
                "An account with this email already exists. If you're sure this is your first time signing up, please contact support — your account may need manual cleanup.",
            },
          };
        }
        // Any other error code (network/unavailable/unauthenticated/etc.) is a
        // transient failure unrelated to a real collision — mislabeling it as
        // "already in use" would send a user with, say, a dropped connection down
        // a dead-end "contact support" path instead of just retrying.
        if (__DEV__) {
          console.error(
            "Email reservation write failed (non-collision):",
            reservationError
          );
        }
        return {
          data: null,
          error: {
            code: reservationError?.code || "unknown",
            message:
              "Something went wrong creating your account. Please try again.",
          },
        };
      }

      // Create user profile in Firestore
      const userProfile = {
        id: user.uid,
        email: email,
        full_name: userData.full_name,
        university: userData.university,
        major: userData.major ?? null,
        graduation_year: userData.graduation_year ?? null,
        role: "user",
        avatar_url: null,
        bio: null,
        push_token: null,
        is_active: true,
        // Legal consent record captured at signup (SignUpScreen requires the
        // acceptance checkbox before this runs). Stored so there's a durable,
        // per-account answer to "did they accept, which version, and when".
        // Timestamp is minted HERE as a Date (→ Firestore Timestamp, same as
        // created_at) rather than an ISO string from the screen, so every
        // *_at field on the doc is the same type for readers doing .toDate().
        terms_accepted: userData.terms_accepted === true,
        terms_accepted_at: userData.terms_accepted === true ? new Date() : null,
        legal_version: userData.legal_version ?? null,
        last_active_at: new Date(),
        created_at: new Date(),
        updated_at: new Date(),
      };

      try {
        await setDoc(doc(db, COLLECTIONS.USERS, user.uid), userProfile);
      } catch (profileError) {
        await rollbackNewAccount();
        throw profileError;
      }

      // Reload the user from Firebase Auth before calling the SMTP API.
      // The Oracle SMTP server looks up the account via Firebase Admin
      // getUserByEmail — reload() confirms the account is fully committed
      // and visible server-side before we make that call.
      await user.reload();

      const { error: verificationError } = await this.sendEmailVerification(email);
      if (verificationError && __DEV__) {
        console.error("Verification email failed:", verificationError.message || verificationError);
      }

      // Return success with user and profile. A verification-send failure is
      // deliberately NOT a signup failure (the account is fully created and
      // Resend can recover) — but it must not be silent either: during the
      // 2026-08-17 relay outage this exact swallow left 12 users staring at
      // "check your email" while every send 400'd server-side, for 29 hours.
      // The flag lets the UI say so on the very first affected signup.
      return {
        data: {
          user,
          profile: userProfile,
          verificationEmailFailed: !!verificationError,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Sign up error:", error);

      let errorMessage = "An error occurred during sign up";

      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "An account with this email already exists";
          break;
        case "auth/invalid-email":
          errorMessage = "Please enter a valid email address";
          break;
        case "auth/weak-password":
          errorMessage = "Password should be at least 6 characters";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection";
          break;
        default:
          errorMessage = "Failed to create account. Please try again";
      }

      return {
        data: null,
        error: { ...error, message: errorMessage },
      };
    }
  }

  // Sign in existing user
  static async signIn(email, password) {
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Get user profile from Firestore
      const { data: profile, error: profileError } = await this.getUserProfile(
        user.uid
      );

      if (profileError || !profile) {
        // User profile not found
        // Sign out the user since profile doesn't exist
        await firebaseSignOut(auth);
        return {
          data: null,
          error: {
            code: "auth/user-profile-not-found",
            message: "Account not found. Please sign up again.",
          },
        };
      }

      return {
        data: { user, profile },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Sign in error:", error);

      let errorMessage = "An error occurred during sign in";

      switch (error.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
        case "auth/invalid-email":
          errorMessage = "Invalid email or password";
          break;
        case "auth/user-disabled":
          errorMessage = "This account has been disabled";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many failed attempts. Please try again later";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection";
          break;
        default:
          errorMessage = "Failed to sign in. Please try again";
      }

      return {
        data: null,
        error: { ...error, message: errorMessage },
      };
    }
  }

  // Sign out user
  static async signOut() {
    try {
      await firebaseSignOut(auth);
      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Sign out error:", error);
      return { error };
    }
  }

  // After a relaunch the SDK reloads the user (emailVerified flips to true)
  // without refreshing the cached ID token, so Firestore rules — which read
  // the token's email_verified claim, not the user object — would keep
  // treating a just-verified student as unverified for up to an hour.
  static async ensureVerifiedClaimIsCurrent(user) {
    if (!user?.emailVerified || verifiedClaimSyncedForUid === user.uid) return;
    try {
      const { claims } = await user.getIdTokenResult();
      if (claims.email_verified !== true) await user.getIdToken(true);
      verifiedClaimSyncedForUid = user.uid;
    } catch (error) {
      if (__DEV__) console.error("Verified-claim token refresh failed:", error);
    }
  }

  // Get current user
  static async getCurrentUser() {
    try {
      const user = auth.currentUser;

      if (!user) {
        return { error: null, user: null };
      }

      await this.ensureVerifiedClaimIsCurrent(user);

      // Add timeout for profile fetch
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Profile fetch timeout")), 5000);
      });

      const profilePromise = this.getUserProfile(user.uid);

      // A slow or failed profile fetch must not look like "not signed in" —
      // degrade to the user without profile, same as the profileError path.
      const { data: profile, error: profileError } = await Promise.race([
        profilePromise,
        timeoutPromise,
      ]).catch((timeoutError) => ({ data: null, error: timeoutError }));

      if (profileError || !profile) {
        // User profile not found for current user
        // Don't fail completely - return user without profile
        return { error: null, user: { ...user, profile: null } };
      }

      return {
        error: null,
        user: { ...user, profile },
      };
    } catch (error) {
      if (__DEV__) console.error("Get current user error:", error);
      // Return null user instead of error to prevent app from crashing
      return { error: null, user: null };
    }
  }

  // Get user profile from Firestore
  static async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));

      if (!userDoc.exists()) {
        return { data: null, error: new Error("User profile not found") };
      }

      const profile = userDoc.data();

      return { data: profile, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user profile error:", error);
      return { data: null, error };
    }
  }

  // Authoritative, non-cached existence check for a users/{uid} profile document.
  // Used by AuthContext's orphaned-account guard to distinguish a genuinely missing
  // profile (a verified Auth account left behind by an incomplete deletion — see
  // scripts/hard-delete-user.js) from a transient read failure (network blip, timeout),
  // which the rest of the app already tolerates. Only `{ exists: false, error: null }`
  // should ever be treated as a confirmed orphan; any `error` means "couldn't tell".
  static async checkProfileExists(userId) {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      return { exists: userDoc.exists(), error: null };
    } catch (error) {
      if (__DEV__) console.error("Check profile exists error:", error);
      return { exists: null, error };
    }
  }

  // Password reset intentionally has no method here — it goes exclusively
  // through the backend SMTP system (ForgotPasswordScreen.js calls
  // /api/forget-password/ directly), not Firebase's built-in
  // sendPasswordResetEmail. Keeping both would silently diverge: Firebase's
  // default reset email bypasses the custom branded template and the
  // deliverability fixes made to the SMTP relay. Don't reintroduce this.

  // Send email verification using custom API
  static async sendEmailVerification(email = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { error: new Error("No user is currently signed in") };
      }

      const userEmail = email || user.email;
      if (!userEmail) {
        return { error: new Error("Email address is required") };
      }

      // Abort after 15s — this call is awaited during signup, and RN's default
      // fetch timeout (~60s+) would hang the signup screen if the API stalls.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      let response;
      try {
        response = await fetch(
          "https://api.try-vibelink.com/api/verify-email/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email: userEmail }),
            signal: controller.signal,
          }
        );
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        // Keep the status on the error: the relay's JSON body replaces the
        // message text, so matching on "429"/"404" inside the message (as this
        // used to) never fired and users saw raw server strings.
        let serverMessage = null;
        try {
          const errorData = await response.json();
          serverMessage = errorData.message || errorData.error || null;
        } catch {
          // Non-JSON body — the status alone is enough.
        }
        const httpError = new Error(serverMessage || `HTTP ${response.status}`);
        httpError.status = response.status;
        throw httpError;
      }

      const data = await response.json().catch(() => ({}));
      return { error: null, data };
    } catch (error) {
      if (__DEV__) console.error("Send email verification error:", error);

      const status = error.status || null;
      let errorMessage;
      if (status === 429) {
        errorMessage = "Too many requests. Please wait before trying again.";
      } else if (status === 404) {
        errorMessage = "We couldn't find your account. Please sign in again.";
      } else if (status) {
        errorMessage =
          "We couldn't send the email right now. Please try again in a moment.";
      } else if (error.name === "AbortError") {
        errorMessage =
          "The email server took too long to respond. Please try resending in a moment.";
      } else {
        errorMessage = "Network error. Please check your connection.";
      }

      // A failed verification send strands a student outside the app and used
      // to be invisible in production (console.error is __DEV__-only). A 429
      // is the throttle doing its job, not a fault. No address in the payload.
      if (status !== 429) {
        try {
          Sentry.captureMessage("verification_email_send_failed", {
            level: "error",
            tags: {
              flow: "verify-email",
              http_status: String(status || error.name || "network"),
            },
          });
        } catch (reportError) {
          // Reporting must never turn this returned error into a throw.
          if (__DEV__) console.error("Sentry report failed:", reportError);
        }
      }

      return { error: { message: errorMessage, status } };
    }
  }

  // Check if email is verified
  static async checkEmailVerification() {
    try {
      const user = auth.currentUser;

      if (!user) {
        return {
          verified: false,
          error: new Error("No user is currently signed in"),
        };
      }

      // Reload user to get latest email verification status
      await user.reload();

      if (user.emailVerified) {
        // The backend marks emailVerified via the Admin SDK, so the cached ID
        // token still carries email_verified: false and the refresh token may
        // have been rotated server-side. Force-refresh credentials now, while
        // a failure can still be surfaced on this screen, instead of letting
        // the next silent refresh sign the user out mid-session.
        try {
          const tokenResult = await user.getIdTokenResult(true);
          if (DIAG_LOGS)
            console.log(
              `[auth] post-verification token refresh ok — email_verified=${tokenResult.claims.email_verified}, expires=${tokenResult.expirationTime}`
            );
          // The refreshed token's claim is what Firestore rules would see —
          // treat it as the source of truth, not the reloaded user object.
          return {
            verified: tokenResult.claims.email_verified === true,
            error: null,
          };
        } catch (refreshError) {
          if (DIAG_LOGS)
            console.log(
              `[auth] post-verification token refresh FAILED — ${refreshError.code || refreshError.message}`
            );
          return {
            verified: false,
            error: new Error(
              "Your email is verified, but your session needs to be renewed. Please sign in again."
            ),
          };
        }
      }

      return { verified: user.emailVerified, error: null };
    } catch (error) {
      if (__DEV__) console.error("Check email verification error:", error);
      return { verified: false, error };
    }
  }

  // Update user profile
  static async updateUserProfile(userId, updates) {
    try {
      // AUTH: Verify caller matches target user — prevents cross-user profile writes (C3)
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== userId) {
        return { error: { message: "Unauthorized: can only update your own profile" } };
      }

      // ALLOWLIST: Only permit safe fields — rejects role, is_active, id, email, created_at (C3)
      const ALLOWED_FIELDS = [
        'full_name',
        'major',
        'graduation_year',
        'bio',
        'avatar_url',
        'push_token',
        'last_active_at',
        'is_active',
        'admin_tour_seen_at',
        'tour_seen',
        'dm_privacy',
      ];

      const sanitized = {};
      for (const key of Object.keys(updates)) {
        if (ALLOWED_FIELDS.includes(key)) {
          sanitized[key] = updates[key];
        }
      }

      // If nothing to update after sanitization, return early
      if (Object.keys(sanitized).length === 0) {
        return { error: { message: "No valid fields to update" } };
      }

      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        ...sanitized,
        updated_at: new Date(),
      });

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Update user profile error:", error);
      return { error };
    }
  }

  // Update user's last active timestamp and mark as active
  static async updateLastActive(userId) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        last_active_at: new Date(),
        is_active: true,
      });
      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Update last active error:", error);
      return { error };
    }
  }

  // Auth state change listener
  static onAuthStateChange(callback) {
    return onAuthStateChanged(auth, async (user) => {
      // A SIGNED_OUT landing seconds after the verified flip is the field
      // evidence for the post-verification session cascade (see DIAG_LOGS).
      if (DIAG_LOGS)
        console.log(
          `[auth] ${new Date().toISOString()} onAuthStateChanged → ${
            user
              ? `SIGNED_IN uid=${user.uid.slice(0, 6)}… verified=${user.emailVerified}`
              : "SIGNED_OUT"
          }`
        );
      if (user) {
        await this.ensureVerifiedClaimIsCurrent(user);
        // Get user profile from Firestore
        const { data: profile, error } = await this.getUserProfile(user.uid);

        if (error || !profile) {
          // User IS authenticated — profile is just unavailable (network issue, timeout).
          // Emit SIGNED_IN with null profile so the app keeps the user logged in
          // and can retry the profile fetch later. Mirrors getCurrentUser() line ~232.
          callback("SIGNED_IN", { user: { ...user, profile: null } });
        } else {
          callback("SIGNED_IN", { user: { ...user, profile } });
        }
      } else {
        callback("SIGNED_OUT", { user: null });
      }
    });
  }

  // Alternative: Delete account without password (if user recently signed in)
  //
  // server-checkin-redesign.md §15.4: this used to own a multi-step cascade
  // (event_attendees pre-check + a rate-limited cleanup callable, a
  // Firestore profile delete, an email-reservation release, a best-effort
  // club_members/notifications/preferences/push_tokens cascade, then a
  // client-side deleteUser()) — four rounds of gate review (§12-§14) kept
  // finding new races in the client-observable gaps between those steps. All
  // of that now runs server-side, atomically, inside the deleteMyAccount
  // Cloud Function (see firebase-functions/eventCheckIn.js's
  // createDeleteMyAccountHandler doc comment for the full ordering). This
  // method's only remaining job is to verify the caller locally, invoke that
  // callable, and — once it succeeds — clear local session state immediately
  // via signOut(), since server-side Admin SDK deletion doesn't itself
  // notify the client's local session synchronously.
  static async deleteAccountWithoutPassword(userId) {
    try {
      const user = auth.currentUser;

      if (!user) {
        return {
          success: false,
          error: { message: "No user is currently signed in" },
        };
      }

      if (user.uid !== userId) {
        return {
          success: false,
          error: { message: "User ID mismatch" },
        };
      }

      await deleteMyAccountCallable();

      // The account (and its Auth session) is now gone server-side — clear
      // the client's local session state right away rather than waiting for
      // the next token refresh to discover it.
      await firebaseSignOut(auth);

      return {
        success: true,
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Delete account error:", error);

      // If requires recent login, return specific error
      if (error.code === "auth/requires-recent-login") {
        return {
          success: false,
          error: {
            code: "auth/requires-recent-login",
            message:
              "For security, please sign in again before deleting your account",
          },
        };
      }

      return {
        success: false,
        error: {
          ...error,
          message: error.message || "Failed to delete account",
        },
      };
    }
  }

  // M23: Change password with reauthentication — never writes password to Firestore
  static async changePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, error: { message: "No user is currently signed in" } };
      }

      if (!user.email) {
        return { success: false, error: { message: "User has no email address" } };
      }

      // Step 1: Reauthenticate with current password to confirm identity
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Step 2: Update password in Firebase Auth (not Firestore)
      await updatePassword(user, newPassword);

      return { success: true, error: null };
    } catch (error) {
      if (__DEV__) console.error("Change password error:", error);

      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        return { success: false, error: { message: "Current password is incorrect" } };
      }
      if (error.code === "auth/weak-password") {
        return { success: false, error: { message: "New password is too weak. Use at least 6 characters." } };
      }
      if (error.code === "auth/requires-recent-login") {
        return { success: false, error: { message: "Session expired. Please sign out and sign back in, then try again." } };
      }

      return { success: false, error: { message: error.message || "Failed to change password" } };
    }
  }
}

export default AuthService;
